import React, { useState } from 'react'
import { ClickAwayListener, Tooltip, useMediaQuery } from '@mui/material'

import styles from './tooltip.module.scss'

interface Props {
	title?: React.ReactNode
	children: React.ReactNode
	className?: string
	maxWidth?: string
	/** By default tooltip stops click propagation (useful inside buttons/menus). Set false when parent needs the click. */
	stopClickPropagation?: boolean
	wrapperWidth?: string
	placement?:
		| 'bottom-end'
		| 'bottom-start'
		| 'bottom'
		| 'left-end'
		| 'left-start'
		| 'left'
		| 'right-end'
		| 'right-start'
		| 'right'
		| 'top-end'
		| 'top-start'
		| 'top'
	/** Растянуть триггер на 100% ширины родителя (flex/grid + disabled-кнопка). */
	fullWidthTrigger?: boolean
	/** На устройствах без hover (тач) открывать/закрывать по нажатию, снаружи — закрытие. */
	tapToOpenOnMobile?: boolean
	/** Controlled open (e.g. selection toolbar). */
	open?: boolean
	onClose?: () => void
	/** Popper offset [skidding, distance]. Negative distance pulls tooltip closer to the anchor. */
	offset?: [number, number]
}
/** Выше оверлеев модалок (см. template.module.scss — 9999) */
const TOOLTIP_POPPER_Z_INDEX = 10050

export const TooltipCustom: React.FC<Props> = ({
	children,
	title,
	placement,
	className,
	maxWidth,
	stopClickPropagation = true,
	wrapperWidth,
	fullWidthTrigger = false,
	tapToOpenOnMobile = false,
	open: controlledOpen,
	onClose,
	offset,
}) => {
	const isTouchPrimary = useMediaQuery('(hover: none)', { noSsr: true })
	const tapMode = Boolean(tapToOpenOnMobile && isTouchPrimary)
	const [tapOpen, setTapOpen] = useState(false)
	const isControlled = controlledOpen !== undefined

	const triggerSpanStyle: React.CSSProperties | undefined = wrapperWidth
		? { display: 'block', width: wrapperWidth, maxWidth: '100%', minWidth: 0 }
		: undefined

	const resolvedOpen = isControlled ? controlledOpen : tapMode ? tapOpen : undefined
	const handleClose = () => {
		if (isControlled) {
			onClose?.()
			return
		}
		if (tapMode) {
			setTapOpen(false)
		}
	}

	const tooltip = (
		<Tooltip
			open={resolvedOpen}
			onClose={isControlled || tapMode ? handleClose : undefined}
			disableHoverListener={isControlled || tapMode}
			disableFocusListener={isControlled || tapMode}
			disableTouchListener={isControlled || tapMode}
			onClick={stopClickPropagation ? (e: React.MouseEvent) => e.stopPropagation() : undefined}
			PopperProps={{
				sx: { zIndex: TOOLTIP_POPPER_Z_INDEX },
				modifiers: offset
					? [
							{
								name: 'offset',
								options: { offset },
							},
						]
					: undefined,
				...(stopClickPropagation
					? {
							onClick(e: React.MouseEvent) {
								e.stopPropagation()
							},
						}
					: {}),
			}}
			componentsProps={{
				tooltip: {
					sx: {
						bgcolor: '#4B4B4B',
						color: '#7F7DF3',
						fontSize: 15,
						borderRadius: '10px',
						maxWidth: maxWidth || 'auto',
						padding: '10px 13px',
						'& .MuiTooltip-arrow': {
							color: '#4B4B4B',
						},
						boxShadow: '0px 0px 4px rgba(0, 0, 0, 0.04), 0px 4px 32px rgba(0, 0, 0, 0.16)',
					},
				},
			}}
			className={className}
			title={title}
			arrow
			placement={placement}
		>
			<span
				className={fullWidthTrigger ? styles.triggerFullWidth : undefined}
				style={triggerSpanStyle}
				onClick={
					tapMode && !isControlled
						? (e) => {
								if (stopClickPropagation) e.stopPropagation()
								setTapOpen((v) => !v)
						  }
						: undefined
				}
			>
				{children}
			</span>
		</Tooltip>
	)

	if (isControlled) {
		return (
			<ClickAwayListener onClickAway={handleClose} mouseEvent='onMouseUp' touchEvent='onTouchEnd'>
				<span className={fullWidthTrigger ? styles.triggerFullWidth : undefined} style={triggerSpanStyle}>
					{tooltip}
				</span>
			</ClickAwayListener>
		)
	}

	return tapMode ? <ClickAwayListener onClickAway={handleClose}>{tooltip}</ClickAwayListener> : tooltip
}
